// src/app/core/services/notifications/notifications.service.ts
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, interval, Observable, switchMap, takeWhile } from 'rxjs';
import { AppSettings } from '../../utils/app-settings';
import { INotification, INotificationResponse } from '../../interfaces/notifications.interface';


@Injectable({
  providedIn: 'root'
})
export class NotificationsService {
  private pollingInterval = 30000; // 30 segundos
  private isPollingActive = false;
  private pollingSubject = new BehaviorSubject<boolean>(false);
  
  constructor(
    private http: HttpClient,
    private appSettings: AppSettings
  ) {}

  /**
   * Obtener todas las notificaciones
   * @returns Observable de lista de notificaciones
   */
  getNotifications(): Observable<INotificationResponse> {
    return this.http.get<INotificationResponse>(this.appSettings.notifications.urls.getNotifications);
  }

  /**
   * Iniciar polling automático de notificaciones
   * @param intervalMs Intervalo en milisegundos (por defecto 30 segundos)
   * @returns Observable que emite las notificaciones actualizadas
   */
  startNotificationPolling(intervalMs: number = this.pollingInterval): Observable<INotificationResponse> {
    this.isPollingActive = true;
    this.pollingSubject.next(true);
    
    return interval(intervalMs).pipe(
      takeWhile(() => this.isPollingActive),
      switchMap(() => this.getNotifications())
    );
  }

  /**
   * Detener el polling automático
   */
  stopNotificationPolling(): void {
    this.isPollingActive = false;
    this.pollingSubject.next(false);
  }

  /**
   * Verificar si el polling está activo
   */
  isPollingActive$(): Observable<boolean> {
    return this.pollingSubject.asObservable();
  }

  /**
   * Marcar notificaciones como leídas
   * @param notificationIds Lista de IDs de notificaciones a marcar
   * @returns Observable de la respuesta del servidor
   */
  markAsRead(notificationIds: number[]): Observable<any> {
    return this.http.patch(this.appSettings.notifications.urls.markAsRead, { notificationIds });
  }

  /**
   * Marcar todas las notificaciones como leídas
   * @returns Observable de la respuesta del servidor
   */
  markAllAsRead(): Observable<any> {
    return this.http.patch(this.appSettings.notifications.urls.markAsRead, { all: true });
  }

  /**
   * Refrescar notificaciones manualmente
   * @returns Observable de la respuesta del servidor
   */
  refreshNotifications(): Observable<INotificationResponse> {
    return this.getNotifications();
  }
}